import { FC, useEffect, useMemo, useState } from 'react';
import type { match as RRMatch } from 'react-router';

import {
    GeneralSettingsIcon,
    HomeIcon,
    ReadIcon,
    SnapsIcon,
    WalletIcon,
} from '@componentLibrary/Icon';
import Button from '@componentLibrary/buttons/Button';
import { clsx } from 'clsx';

import { useGetPendingSparksCountQuery } from '@core/sparks/queries/GetPendingSparksQuery';

import { useAccountLinks } from '@features/account-links/queries';
import { useGetAllAccountCourses } from '@features/courses/queries/useGetAllCourses';
import { useGetEventCountQuery } from '@features/events/queries';

import {
    AccountCircle as AccountCircleIcon,
    Event as EventIcon,
    HelpOutline as HelpIcon,
    People as PeopleIcon,
    OfflineBolt as SparkIcon,
    SupervisorAccount,
    TrendingUp,
} from '@components/icons';
import UserWidget from '@components/layout/AppHeader/UserWidget';
import { SparkplugCCLogo, SparkplugLogo } from '@components/logos';
import SwitchUserViewModal from '@components/overlays/SwitchUserViewModal';
import { Link, useLocation } from '@components/router';

import { useApp, useControlCenterApp } from '@hooks/AppHooks';
import { useSparkplugAccount } from '@hooks/SparkplugAccountsHooks/SparkplugAccountsHooks';

import { useCurrentUserWallet } from '@app/employee-app/features/wallet/queries';

import CalloutMessage from '../CalloutMessage';
import { NoticeBar } from '../NoticeBar';
import { AppHeaderProps, AppLogoProps, NavLinkItem } from './AppHeader.types';
import CustomNavLink from './CustomNavLink';

import './AppHeader.scss';

const AppLogo: FC<AppLogoProps> = ({ isControlCenterApp }) => {
    const to = !isControlCenterApp ? '/' : '/control-center';
    const Logo = isControlCenterApp ? SparkplugCCLogo : SparkplugLogo;

    return (
        <div className="app-logo">
            <Link to={to}>
                <Logo />
            </Link>
        </div>
    );
};

export const AppHeader: FC<AppHeaderProps> = ({
    isControlCenterApp,
    isAdminApp,
    appIsMobile,
    user,
    account,
    pendingSparksCount,
    unclaimedDepositCount,
    pendingEventCount,
    pendingVendorLinkCount,
    hasCourses,
    hasEvents,
    isPWA,
    connectEnabled,
}) => {
    const isEmployeeApp = !isAdminApp;
    const adminLoggedInAsParticipant =
        isEmployeeApp && user?.role === 'retailer-admin' && !appIsMobile;
    const [showSwitchUserViewModal, setShowSwitchUserViewModal] = useState(false);
    const isSuperAdmin = isAdminApp && user?.role === 'super-admin';
    const subscriptionTypeIsPaid = account?.metaData?.subscriptionType === 'paid';
    const pathname = useLocation().pathname;
    const isOnboarding = pathname.includes('/onboarding');

    const paywallAlert = useMemo(() => {
        if (!isSuperAdmin) {
            return false;
        }

        const hasPaymentMethods = !!account?.paymentHealth?.hasPaymentMethods;
        const hasNoFailedPayment = account?.paymentHealth?.latestChargeStatus !== 'failed';

        if (!hasPaymentMethods && subscriptionTypeIsPaid) {
            return 'has no payment methods on file';
        }
        if (!hasNoFailedPayment && subscriptionTypeIsPaid) {
            return 'has a failed payment';
        }
        return false;
    }, [account]);

    const NavLinks = useMemo(() => {
        if (isControlCenterApp) {
            return [];
        }

        const allLinks: NavLinkItem[] =
            user && account
                ? [
                      {
                          Icon: TrendingUp,
                          url: `/${account._id}/dashboard`,
                          text: 'Dashboard',
                          isVisible: () => isAdminApp,
                          isActive: (match: RRMatch) => {
                              return match != null;
                          },
                      },
                      {
                          Icon: SparkIcon,
                          url:
                              +pendingSparksCount > 0
                                  ? `/${account._id}/sparks/inbox`
                                  : `/${account._id}/sparks`,
                          badgeCount: pendingSparksCount,
                          text: 'Sparks',
                          isVisible: () => isAdminApp,
                          isActive: (match: RRMatch, location: any) => {
                              return (
                                  match != null ||
                                  location.pathname.includes(`/${account._id}/sparks`)
                              );
                          },
                      },
                      {
                          Icon: ReadIcon,
                          url: `/${account._id}/courses`,
                          text: 'Courses',
                          chipText: hasCourses ? undefined : 'NEW',
                          isVisible: () => isAdminApp && !appIsMobile,
                          isActive: (match: RRMatch) => {
                              return match != null;
                          },
                      },
                      {
                          Icon: EventIcon,
                          url:
                              account?.type === 'retailer' && +pendingEventCount > 0
                                  ? `/${account._id}/events/inbox?requestStatus=pending`
                                  : `/${account._id}/events`,
                          text: 'Events',
                          ...(account?.type === 'retailer' && { badgeCount: pendingEventCount }),
                          chipText: hasEvents ? undefined : 'NEW',
                          isVisible: () =>
                              isAdminApp &&
                              !appIsMobile &&
                              import.meta.env.REACT_APP_EVENTS === 'true',
                          isActive: (match: RRMatch, location: any) => {
                              return (
                                  match != null ||
                                  location.pathname.includes(`/${account._id}/events`)
                              );
                          },
                      },
                      {
                          Icon: SnapsIcon,
                          url: `/${account._id}/snaps`,
                          text: 'Snaps',
                          chipText: '',
                          isVisible: () => isAdminApp && !appIsMobile,
                          isActive: (match: RRMatch) => {
                              return match != null;
                          },
                      },
                      {
                          Icon: EventIcon,
                          url: `/${account._id}/events`,
                          text: 'Events',
                          isVisible: () => appIsMobile && !isEmployeeApp,
                          isActive: (match: RRMatch) => {
                              return match != null;
                          },
                      },
                      {
                          Icon: SupervisorAccount,
                          url:
                              account?.type === 'retailer' && +pendingVendorLinkCount > 0
                                  ? `/${account._id}/partners/inbox`
                                  : `/${account._id}/partners`,
                          text: 'Partners',
                          ...(account?.type === 'retailer' && {
                              badgeCount: pendingVendorLinkCount,
                          }),
                          isVisible: () => isAdminApp && connectEnabled,
                          isActive: (match: RRMatch, location: any) => {
                              return (
                                  match != null ||
                                  location.pathname.includes(`/${account._id}/partners`)
                              );
                          },
                      },
                      {
                          Icon: HomeIcon,
                          url: `/user/sparks`,
                          text: 'Home',
                          isVisible: () => isEmployeeApp,
                          isActive: (match: RRMatch) => {
                              return match != null;
                          },
                      },
                      {
                          Icon: WalletIcon,
                          url: `/user/wallet`,
                          text: 'Wallet',
                          badgeCount: unclaimedDepositCount,
                          isVisible: () => isEmployeeApp,
                          isActive: (match: RRMatch) => !!match,
                      },
                      {
                          Icon: GeneralSettingsIcon,
                          url: `/${account._id}/settings`,
                          text: 'Settings',
                          isVisible: () => isAdminApp,
                          isActive: (match: RRMatch, location: any) => {
                              return location.pathname.includes(`/${account._id}/settings`);
                          },
                      },
                      {
                          Icon: AccountCircleIcon,
                          url: `/user/profile`,
                          text: 'Profile',
                          isVisible: () => isEmployeeApp,
                          isActive: (match: RRMatch) => {
                              return match != null;
                          },
                      },
                      {
                          Icon: HelpIcon,
                          url: `/user/help`,
                          text: 'Help',
                          isVisible: () => appIsMobile && isEmployeeApp,
                          isActive: (match: RRMatch) => {
                              return match != null;
                          },
                      },
                  ]
                : [];

        return allLinks.filter((link) => link != null && link.isVisible());
    }, [
        appIsMobile,
        isControlCenterApp,
        user,
        account,
        pendingSparksCount,
        unclaimedDepositCount,
        pendingEventCount,
        pendingVendorLinkCount,
        hasCourses,
        hasEvents,
    ]);

    useEffect(() => {
        if (NavLinks.length > 0) {
            document.body.classList.add('app-has-navlinks');
        } else {
            document.body.classList.remove('app-has-navlinks');
        }
    }, [NavLinks.length]);

    if (isOnboarding) {
        return null;
    }

    return (
        <>
            <div className="app-header">
                {adminLoggedInAsParticipant && (
                    <NoticeBar
                        message="You are currently logged in as a participant"
                        actionButton={
                            <Button
                                onClick={() => setShowSwitchUserViewModal(true)}
                                startIcon={<PeopleIcon />}
                                variant="outlined"
                                color="blue"
                            >
                                Switch View
                            </Button>
                        }
                    />
                )}
                <div className="app-navbar">
                    <AppLogo isControlCenterApp={isControlCenterApp} />
                    {NavLinks.length > 0 && (
                        <ul
                            className={clsx(['app-navbar-menu', isPWA ? 'pwa-bottom-padding' : ''])}
                        >
                            {NavLinks.map(
                                ({ url, text, isActive, Icon, badgeCount = 0, chipText }) => (
                                    <CustomNavLink
                                        key={url}
                                        url={url}
                                        text={text}
                                        isActive={isActive}
                                        icon={<Icon />}
                                        badgeCount={badgeCount}
                                        chipText={chipText}
                                    />
                                ),
                            )}
                        </ul>
                    )}

                    <UserWidget account={account} switchUserViewFn={setShowSwitchUserViewModal} />
                    <SwitchUserViewModal
                        isVisible={showSwitchUserViewModal}
                        onClose={() => {
                            setShowSwitchUserViewModal(false);
                        }}
                    />
                </div>
                {paywallAlert && (
                    <CalloutMessage
                        color="red"
                        message={`This account ${paywallAlert} and is being shown a paywall.`}
                    />
                )}
            </div>
        </>
    );
};

export default () => {
    const { user, appIsMobile, isAdminApp, isPWA } = useApp();
    const { isControlCenterApp } = useControlCenterApp();

    const { account, connectEnabled } = useSparkplugAccount();
    const { count: pendingSparksCount = 0 } = useGetPendingSparksCountQuery({
        account,
        isAdminApp,
    });

    const { data: pendingEventCount = 0 } = useGetEventCountQuery({
        accountId: account?._id ?? '',
        variant: 'inbox',
        enabled: account?.type === 'retailer',
    });

    const { data: totalEventCount = 0 } = useGetEventCountQuery({
        accountId: account?._id ?? '',
        variant: 'all',
        enabled: Boolean(account?._id),
    });
    const hasEvents = totalEventCount > 0;

    const { accountLinks: pendingVendorLinks = [] } = useAccountLinks(
        account?._id ?? '',
        isAdminApp && !!account && account.type === 'retailer',
        ['pending'],
    );
    const pendingVendorLinkCount = pendingVendorLinks.length;

    const { courses } = useGetAllAccountCourses({
        accountId: account?._id,
        limit: '1',
    });
    const hasCourses = courses.length > 0;

    const { currentUserWalletIsReady, currentUserWallet } = useCurrentUserWallet();

    const unclaimedDepositCount = useMemo(() => {
        return (
            currentUserWallet?.deposits?.filter(({ status }) => status !== 'Claimed')?.length ?? 0
        );
    }, [currentUserWalletIsReady, currentUserWallet, account]);

    const props = {
        isControlCenterApp,
        isAdminApp,
        appIsMobile,
        user,
        account,
        pendingSparksCount,
        pendingEventCount,
        pendingVendorLinkCount,
        hasCourses,
        hasEvents,
        unclaimedDepositCount,
        isPWA,
        connectEnabled,
    };

    return <AppHeader {...props} />;
};
